import { Area, AreaChart, CartesianGrid, LabelList, XAxis } from 'recharts'
import {
  Search,
  CalendarDays,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Hash,
  Filter,
  CircleSlash,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Badge } from '@/shared/components/ui/badge'
import { useBusquedasSueroterapia } from '../hooks/useSueroterapia'
import type { RankingItem } from '../types'
import { AZUL, VERDE, MORADO, ROSA, KpiCard, formatoFecha } from './KpiCard'

const chartConfig = {
  busquedas: {
    label: 'Búsquedas',
    color: AZUL,
  },
} satisfies ChartConfig

interface RankingCardProps {
  icon: React.ElementType
  title: string
  items: RankingItem[]
  color: string
  emptyLabel?: string
}

function RankingCard({ icon: Icon, title, items, color, emptyLabel }: RankingCardProps) {
  const nf = (n: number) => n.toLocaleString('es-EC')
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1
  return (
    <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color }} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {items.length > 0 ? (
          items.slice(0, 8).map((it, idx) => {
            const pct = (it.count / max) * 100
            return (
              <div key={`${it.label}-${idx}`} className="space-y-1">
                <div className="flex items-center justify-between text-sm gap-3">
                  <span className="font-medium text-foreground truncate" title={it.label}>
                    {it.label || '—'}
                  </span>
                  <span className="text-xs font-semibold shrink-0" style={{ color }}>
                    {nf(it.count)}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                  <div
                    className="h-2 rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            )
          })
        ) : (
          <p className="text-xs text-muted-foreground py-4 text-center">
            {emptyLabel ?? 'Sin datos en el período.'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

interface BusquedasTabProps {
  dias: number
}

export function BusquedasTab({ dias }: BusquedasTabProps) {
  const { data, isLoading, error } = useBusquedasSueroterapia(dias)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el reporte de búsquedas. Verifique que la integración de Sueroterapia
          esté configurada y activa, e intente nuevamente.
        </p>
      </div>
    )
  }

  const nf = (n: number) => n.toLocaleString('es-EC')

  const totalBusquedas = data.busquedas_por_dia.reduce((acc, p) => acc + p.count, 0)
  const promedioDiario = dias > 0 ? totalBusquedas / dias : 0
  const totalSinResultados = data.top_sin_resultados.reduce((acc, it) => acc + it.count, 0)

  const areaChartData = data.busquedas_por_dia.map((p) => ({
    fecha: p.fecha,
    busquedas: p.count,
  }))

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-xs flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatoFecha(data.rango.desde)} — {formatoFecha(data.rango.hasta)}
        </Badge>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <RefreshCw className="h-3 w-3" /> datos en vivo
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          icon={Search}
          label="Total de búsquedas"
          value={nf(totalBusquedas)}
          sub="Búsquedas en el período"
          color={AZUL}
        />
        <KpiCard
          icon={CalendarDays}
          label="Promedio diario"
          value={promedioDiario.toFixed(1)}
          sub={`Sobre ${dias} días`}
          color={VERDE}
        />
        <KpiCard
          icon={AlertTriangle}
          label="Sin resultados"
          value={nf(totalSinResultados)}
          sub="Búsquedas sin coincidencias"
          color={ROSA}
        />
      </div>

      {/* Búsquedas por día */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Búsquedas por día</CardTitle>
        </CardHeader>
        <CardContent>
          {areaChartData.some((p) => p.busquedas > 0) ? (
            <ChartContainer config={chartConfig} className="!aspect-auto h-[250px] w-full">
              <AreaChart
                accessibilityLayer
                data={areaChartData}
                margin={{ left: 12, right: 12, top: 20 }}
              >
                <defs>
                  <linearGradient id="fillBusquedasSuero" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-busquedas)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-busquedas)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="fecha"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={24}
                  tickFormatter={(value) => {
                    const date = new Date(value)
                    return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })
                  }}
                />
                <ChartTooltip
                  cursor={{ stroke: AZUL, strokeWidth: 1, strokeDasharray: '4 4' }}
                  content={<ChartTooltipContent indicator="line" />}
                />
                <Area
                  dataKey="busquedas"
                  type="natural"
                  fill="url(#fillBusquedasSuero)"
                  fillOpacity={1}
                  stroke="var(--color-busquedas)"
                  strokeWidth={2}
                >
                  <LabelList
                    dataKey="busquedas"
                    position="top"
                    offset={10}
                    className="fill-foreground"
                    fontSize={11}
                    formatter={(value: number) => (value > 0 ? value : '')}
                  />
                </Area>
              </AreaChart>
            </ChartContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-muted-foreground">
              <div className="text-center space-y-2">
                <CircleSlash className="h-12 w-12 mx-auto" />
                <p>Sin búsquedas en el período</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankingCard icon={Search} title="Términos más buscados" items={data.top_queries} color={AZUL} />
        <RankingCard
          icon={CircleSlash}
          title="Búsquedas sin resultados"
          items={data.top_sin_resultados}
          color={ROSA}
          emptyLabel="Ninguna búsqueda quedó sin resultados. 🎉"
        />
        <RankingCard icon={Hash} title="Códigos CIE-10 más consultados" items={data.top_cie10} color={MORADO} />
        <RankingCard
          icon={Filter}
          title="Filtros de protocolo más usados"
          items={data.top_protocol_filter}
          color={VERDE}
        />
      </div>
    </div>
  )
}
